import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { Tenant, Hostel, PageResponse, TenantStatus } from '@/api/types';
import { UserCheck, ChevronDown, ChevronUp, LogOut, Download } from 'lucide-react';
import { TenantExportDialog } from '@/components/TenantExportDialog';
import { SignTermsOnBehalfInline } from '@/components/SignTermsOnBehalfInline';
import { Select } from '@/components/ui/Select';
import { Pagination } from '@/components/ui/Pagination';
import { usePagination } from '@/hooks/usePagination';

const STATUS_COLORS: Record<string, string> = {
  ACTIVE:      'bg-green-100 text-green-800',
  RESERVED:    'bg-amber-100 text-amber-800',
  CHECKED_OUT: 'bg-gray-100 text-gray-600',
  CANCELLED:   'bg-red-100 text-red-700',
};

function CheckInInline({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [joiningDate, setJoiningDate] = useState(new Date().toISOString().split('T')[0]);
  const mutation = useMutation({
    mutationFn: () => client.post(`/api/tenants/${tenantId}/checkin`, { joiningDate }),
    onSuccess: onDone,
  });
  return (
    <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl p-3 flex flex-wrap items-end gap-3"
      onClick={e => e.stopPropagation()}>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.actualJoiningDate', 'Actual Joining Date')}</label>
        <input type="date" value={joiningDate} onChange={e => setJoiningDate(e.target.value)}
          className="border rounded-lg px-3 py-1.5 text-sm" />
      </div>
      <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
        className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
        {mutation.isPending ? t('supervisor.checkingIn', 'Checking In…') : t('supervisor.confirmCheckin', 'Confirm Check-In')}
      </button>
      {mutation.isError && <p className="w-full text-red-600 text-xs">{(mutation.error as Error).message}</p>}
    </div>
  );
}

function CancelReservationInline({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const mutation = useMutation({
    mutationFn: () => client.delete(`/api/tenants/${tenantId}/reservation`),
    onSuccess: onDone,
  });
  return (
    <button onClick={e => { e.stopPropagation(); mutation.mutate(); }} disabled={mutation.isPending}
      className="shrink-0 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1 rounded-lg text-xs font-medium disabled:opacity-50">
      {mutation.isPending ? t('supervisor.cancelling', 'Cancelling…') : t('common.cancel')}
    </button>
  );
}

function CheckoutInline({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [checkoutDate, setCheckoutDate] = useState(new Date().toISOString().split('T')[0]);
  const mutation = useMutation({
    mutationFn: () => client.post(`/api/tenants/${tenantId}/checkout`, { checkoutDate }),
    onSuccess: onDone,
  });
  return (
    <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 flex flex-wrap items-end gap-3"
      onClick={e => e.stopPropagation()}>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('supervisor.checkoutDate', 'Check-Out Date')}</label>
        <input type="date" value={checkoutDate} onChange={e => setCheckoutDate(e.target.value)}
          className="border rounded-lg px-3 py-1.5 text-sm" />
      </div>
      <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
        className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
        {mutation.isPending ? t('supervisor.processing', 'Processing…') : t('supervisor.confirmCheckout', 'Confirm Check-Out')}
      </button>
      {mutation.isError && <p className="w-full text-red-600 text-xs">{(mutation.error as Error).message}</p>}
    </div>
  );
}

export function SupervisorHostelTenantsPage() {
  const { t } = useTranslation();
  const { id: hostelId } = useParams({ from: '/protected/supervisor/hostels/$id/tenants' });
  const navigate = useNavigate();
  const urlSearch = useSearch({ from: '/protected/supervisor/hostels/$id/tenants' });
  const qc = useQueryClient();

  const status = urlSearch.status as TenantStatus | '';
  const [searchText, setSearchText] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const { page, size, setPage, resetPage } = usePagination();

  const { data: hostel } = useQuery<Hostel>({
    queryKey: ['hostel', hostelId],
    queryFn: () => client.get(`/api/hostels/${hostelId}`).then(r => r as unknown as Hostel),
  });

  const { data, isLoading, isError, error } = useQuery<PageResponse<Tenant>>({
    queryKey: ['sup-tenants', hostelId, status, searchText, page],
    queryFn: () => {
      const params = new URLSearchParams({ hostelId, page: String(page), size: String(size) });
      if (status) params.set('status', status);
      if (searchText) params.set('search', searchText);
      return client.get(`/api/tenants?${params}`).then(r => r as unknown as PageResponse<Tenant>);
    },
  });

  const tenants = data?.content ?? [];

  function changeStatus(val: string) {
    resetPage();
    void navigate({
      to: '/supervisor/hostels/$id/tenants',
      params: { id: hostelId },
      search: { status: val },
    });
  }

  function refresh() {
    setExpanded(null);
    void qc.invalidateQueries({ queryKey: ['sup-tenants'] });
    void qc.invalidateQueries({ queryKey: ['sup-hostel-stats', hostelId] });
  }

  const statusLabel: Record<string, string> = {
    ACTIVE:      t('supervisor.activeTenantsTitle', 'Active Tenants'),
    RESERVED:    t('supervisor.reservedTenantsTitle', 'Reserved Tenants'),
    CHECKED_OUT: t('supervisor.checkedOutTenantsTitle', 'Checked-Out Tenants'),
    CANCELLED:   t('supervisor.cancelledReservationsTitle', 'Cancelled Reservations'),
    '':          t('supervisor.allTenantsTitle', 'All Tenants'),
  };

  const title = statusLabel[status] ?? t('tenant.title');

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Link to="/supervisor/hostels/$id" params={{ id: hostelId }} search={{ bedStatus: '' }} className="text-violet-600 text-sm hover:underline">
          ← {t('supervisor.hostel', 'Hostel')}
        </Link>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-gray-900">{title}</h1>
        <div className="ml-auto flex items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('common.search', 'Search')}</label>
            <input
              value={searchText}
              onChange={e => { setSearchText(e.target.value); resetPage(); }}
              placeholder={t('supervisor.searchNamePhone', 'Search name / phone…')}
              className="h-9 border border-gray-300 rounded-lg px-3 text-sm w-44"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('common.status')}</label>
            <Select
              value={status}
              onChange={e => changeStatus(e.target.value)}
              className="w-auto h-9"
              options={[
                { value: '', label: t('common.all') },
                { value: 'ACTIVE', label: t('common.active') },
                { value: 'RESERVED', label: t('common.reserved') },
                { value: 'CHECKED_OUT', label: t('common.checkedOut') },
                { value: 'CANCELLED', label: t('common.cancelled') },
              ]}
            />
          </div>
          <button
            onClick={() => setExportOpen(true)}
            className="h-9 flex items-center gap-1.5 border border-violet-300 text-violet-700 hover:bg-violet-50 px-3 rounded-lg text-xs font-medium transition"
          >
            <Download size={13} /> {t('export.button', 'Export')}
          </button>
        </div>
      </div>

      {exportOpen && (
        <TenantExportDialog
          hostelId={hostelId}
          status={status}
          accent="violet"
          onClose={() => setExportOpen(false)}
        />
      )}

      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-red-700 text-sm">
          {t('supervisor.failedToLoadTenants', 'Failed to load tenants:')} {(error as Error)?.message ?? t('supervisor.unknownError', 'Unknown error')}.
        </div>
      )}

      {isLoading ? (
        <div className="bg-white rounded-xl p-10 text-center border border-gray-100">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-sm">{t('supervisor.loadingTitle', 'Loading {{title}}…', { title: title.toLowerCase() })}</p>
        </div>
      ) : tenants.length === 0 ? (
        <div className="bg-white rounded-xl p-10 text-center border border-dashed border-gray-200">
          <UserCheck size={36} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">{t('supervisor.noTitleFound', 'No {{title}} found', { title: title.toLowerCase() })}</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-400 mb-3">{t('supervisor.tenantCountTapForDetails', '{{count}} tenant{{s}} — tap a row for details', { count: tenants.length, s: tenants.length !== 1 ? 's' : '' })}</p>
          <div className="space-y-2">
            {tenants.map(tn => (
              <div key={tn.id} className="bg-white rounded-xl shadow-sm border border-gray-100 hover:border-violet-300 hover:shadow-md transition">
                <button
                  onClick={() => setExpanded(expanded === tn.id ? null : tn.id)}
                  className="w-full px-4 py-3 text-left flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 font-bold text-lg shrink-0 overflow-hidden">
                    {tn.photoUrl
                      ? <img src={tn.photoUrl} alt={tn.fullName} className="w-full h-full object-cover" />
                      : tn.fullName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{tn.fullName}</p>
                    <p className="text-xs text-gray-500">{tn.phone}{tn.email ? ` · ${tn.email}` : ''}</p>
                    {(tn.roomNumber || tn.bedLabel) && (
                      <p className="text-xs text-violet-600 font-medium mt-0.5">
                        {tn.roomNumber ? t('supervisor.roomNumberLabel', 'Room {{number}}', { number: tn.roomNumber }) : ''}{tn.bedLabel ? ` · ${t('supervisor.bedLabel', 'Bed {{label}}', { label: tn.bedLabel })}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold text-violet-700 text-sm">₹{Number(tn.monthlyRent).toLocaleString()}/mo</p>
                    <p className="text-xs text-gray-400">{t('supervisor.joinedOn', 'Joined {{date}}', { date: tn.joiningDate })}</p>
                    {tn.checkoutDate && (
                      <p className="text-xs text-red-400">{t('supervisor.leftOn', 'Left {{date}}', { date: tn.checkoutDate })}</p>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${STATUS_COLORS[tn.status] ?? 'bg-gray-100 text-gray-600'}`}>
                    {tn.status.replace('_', ' ')}
                  </span>
                  {tn.status === 'RESERVED' ? (
                    <button
                      onClick={e => { e.stopPropagation(); setExpanded(expanded === tn.id ? null : tn.id); }}
                      className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white px-3 py-1 rounded-lg text-xs font-medium"
                    >
                      {t('tenant.checkIn')}
                    </button>
                  ) : tn.status === 'ACTIVE' ? (
                    <button
                      onClick={e => { e.stopPropagation(); setExpanded(expanded === tn.id ? null : tn.id); }}
                      className="shrink-0 flex items-center gap-1 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1 rounded-lg text-xs font-medium"
                    >
                      <LogOut size={12} /> {t('tenant.checkOut')}
                    </button>
                  ) : (
                    expanded === tn.id ? <ChevronUp size={16} className="text-gray-300 shrink-0" /> : <ChevronDown size={16} className="text-gray-300 shrink-0" />
                  )}
                </button>
                {expanded === tn.id && (
                  <div className="px-4 pb-4">
                    {tn.status === 'RESERVED' && (
                      <div className="flex items-start justify-between gap-2">
                        <CheckInInline tenantId={tn.id} onDone={refresh} />
                        <CancelReservationInline tenantId={tn.id} onDone={refresh} />
                      </div>
                    )}
                    {tn.status === 'ACTIVE' && <CheckoutInline tenantId={tn.id} onDone={refresh} />}
                    {!tn.termsAccepted && tn.status !== 'CHECKED_OUT' && tn.status !== 'CANCELLED' && (
                      <div className="mt-3">
                        <SignTermsOnBehalfInline tenantId={tn.id} hostel={hostel ?? null} onDone={refresh} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination
            page={page}
            totalPages={data?.totalPages ?? 0}
            totalElements={data?.totalElements ?? 0}
            size={size}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
